/**
 * THE PORTAL DESIGNER — one editor for every screen, generated from the slots.
 *
 * Reused unchanged, not rebuilt: `ImageField` (upload + Brand library), the
 * font and text-size vocabulary from lib/designTokens, and the shadcn inputs.
 * The slot rows come from components/loyalty/SlotEditor.jsx.
 *
 * THE PREVIEW RENDERS THE REAL PORTAL: it mounts the same
 * components/loyalty/PortalView.jsx the member's phone mounts, fed with the
 * unsaved draft. It is not a mock-up, so it cannot drift.
 *
 * Nothing reaches a member until Save is pressed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlignCenter, AlignLeft, AlignRight, Bold, Italic, Loader2, Save,
    Smartphone, Tablet } from 'lucide-react';
import { ImageField } from '../goalgame/admin/ImageField';
import { TEXT_SIZES } from '../../lib/designTokens';
import { FontPicker } from '../design/FontPicker';
import { TYPOGRAPHY_DEFAULTS } from './typography';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import PortalView from './PortalView';
import { SlotEditor } from './SlotEditor';

const field = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900";

const SAMPLE = {
    me: { points: 2000, dollar_value: 20, points_remaining: 3000,
          member: { first_name: 'Alex' } },
    saved: { first_name: 'Alex', email: 'alex@example.com' },
    form: { first_name: 'Alex', email: 'alex@example.com', pin: '', consent: true },
};

const Colour = ({ label, value, onChange, testid }) => (
    <label className="block min-w-0">
        <span className="mb-1 block break-words text-[11px] text-slate-500">{label}</span>
        <input type="color" value={value || '#000000'} data-testid={testid}
               onChange={(e) => onChange(e.target.value)}
               className="h-9 w-full max-w-[64px] rounded border border-slate-200 bg-transparent dark:border-slate-700" />
    </label>
);

/** THE WIDTH THE EDITOR ITSELF HAS — not the width of the screen. With the
 *  sidebar open an iPad gives this editor about 800px while the browser still
 *  reports 1194, which is what used to squeeze the controls into a sliver. */
const useOwnWidth = () => {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        setWidth(Math.round(el.getBoundingClientRect().width));
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, width];
};

const Frame = ({ width, height, scale, testid, children }) => (
    <div className="overflow-hidden rounded-[28px] border-4 border-slate-800 bg-black shadow-xl"
         style={{ width: Math.round(width * scale), height: Math.round(height * scale) }}
         data-testid={testid}>
        {/* The screen scrolls INSIDE the frame, so a long screen still reaches
            its last button. */}
        <div className="overflow-y-auto overflow-x-hidden"
             style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            {children}
        </div>
    </div>
);

/** ONE read-only line: what the one-time look step did for THIS clinic.
 *  Super admin only — any other role is refused by the server and sees
 *  nothing at all. It writes nothing and changes no design. Remove with the
 *  task. */
const LookButton = ({ orgId, t }) => {
    const [row, setRow] = useState(null);
    const [ask, setAsk] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = () => api.get('/loyalty/vp-look-status', { params: { organization_id: orgId } })
        .then((r) => setRow(r.data))
        .catch(() => setRow(null));
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId]);
    // Anyone who is not a super admin is refused by the server and sees nothing.
    if (!row) return null;
    const applied = row.state === 'applied';

    const run = async (undoIt) => {
        setBusy(true);
        try {
            await api.post(`/loyalty/vp-look-${undoIt ? 'undo' : 'apply'}`, null,
                { params: { organization_id: orgId } });
            await load();
            window.location.reload();       // so the preview shows the change
        } finally { setBusy(false); setAsk(false); }
    };

    if (applied) {
        return (
            <Button size="sm" variant="outline" disabled={busy}
                    data-testid="cc-loyalty-look-undo" onClick={() => run(true)}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                    : t('loyalty.design.lookStep.undoNow', 'Undo luxury look')}
            </Button>
        );
    }
    if (!ask) {
        return (
            <Button size="sm" variant="outline" disabled={busy}
                    data-testid="cc-loyalty-look-apply" onClick={() => setAsk(true)}>
                {t('loyalty.design.lookStep.applyNow', 'Apply the luxury look')}
            </Button>
        );
    }
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="cc-loyalty-look-ask">
            <span className="text-[11px] text-slate-500">
                {t('loyalty.design.lookStep.applyAsk',
                    'This overwrites the button colours of this clinic. The previous design is saved first.')}
            </span>
            <Button size="sm" disabled={busy} data-testid="cc-loyalty-look-apply-go"
                    onClick={() => run(false)}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                    : t('common.confirm', 'Confirm')}
            </Button>
            <Button size="sm" variant="ghost" data-testid="cc-loyalty-look-apply-cancel"
                    onClick={() => setAsk(false)}>
                {t('common.cancel', 'Cancel')}
            </Button>
        </div>
    );
};

/** ONE row per text style: font, size, bold, italic, alignment, colour.
 *
 *  AT MODULE LEVEL ON PURPOSE. Declared inside the editor it was a NEW
 *  component type on every render, so React threw the row away and built it
 *  again — and the Command Center refreshes its tiles every 5 seconds, which
 *  is why an open font list shut itself after a few seconds. Out here the row
 *  keeps its identity and the list stays open until you choose or click away.
 */
const StyleRow = ({ id, label, withAlign = false, colourKey, theme, setT, t }) => {
    const toggle = (key, Icon, testid) => (
        <button type="button" data-testid={testid}
                onClick={() => setT(key)(!theme[key])}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border text-xs
                            ${theme[key] ? 'border-fuchsia-500 bg-fuchsia-500 text-white'
                                : 'border-slate-300 dark:border-slate-600'}`}>
            <Icon className="h-3.5 w-3.5" />
        </button>
    );
    const alignBtn = (value, Icon) => (
        <button key={value} type="button" data-testid={`cc-loyalty-type-${id}-align-${value}`}
                onClick={() => setT(`${id}_align`)(theme[`${id}_align`] === value ? '' : value)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border
                            ${theme[`${id}_align`] === value
                                ? 'border-fuchsia-500 bg-fuchsia-500 text-white'
                                : 'border-slate-300 dark:border-slate-600'}`}>
            <Icon className="h-3.5 w-3.5" />
        </button>
    );
    return (
        <div className="flex min-w-0 flex-wrap items-end gap-2"
             data-testid={`cc-loyalty-type-row-${id}`}>
            <div className="min-w-[150px] flex-1">
                <span className="mb-1 block break-words text-[11px] text-slate-500">{label}</span>
                <FontPicker value={theme[`${id}_font`] || ''}
                            onChange={(key) => setT(`${id}_font`)(key)}
                            label={t('loyalty.design.type.font', 'Font')}
                            testid={`cc-loyalty-type-${id}-font`} />
            </div>
            <select className={`${field} w-28`} value={theme[`${id}_size`] ?? ''}
                    data-testid={`cc-loyalty-type-${id}-size`}
                    onChange={(e) => setT(`${id}_size`)(e.target.value)}>
                {(id === 'button' || id === 'small') && (
                    <option value="">{t('loyalty.design.type.sameAsBody', 'Same as body')}</option>
                )}
                {TEXT_SIZES.filter((s) => s.key).map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>))}
            </select>
            {toggle(`${id}_bold`, Bold, `cc-loyalty-type-${id}-bold`)}
            {toggle(`${id}_italic`, Italic, `cc-loyalty-type-${id}-italic`)}
            {withAlign && (
                <div className="flex gap-1" data-testid={`cc-loyalty-type-${id}-align`}>
                    {alignBtn('left', AlignLeft)}
                    {alignBtn('center', AlignCenter)}
                    {alignBtn('right', AlignRight)}
                </div>
            )}
            <Colour label={t('loyalty.design.type.colour', 'Colour')}
                    value={theme[colourKey || `${id}_colour`]}
                    onChange={setT(colourKey || `${id}_colour`)}
                    testid={`cc-loyalty-type-${id}-colour`} />
        </div>
    );
};

export const DesignEditor = ({ orgId, cfg, onSave, busy, startScreen }) => {
    const { t } = useTranslation();
    const [branding, setBranding] = useState(cfg.design?.branding || {});
    const [theme, setTheme] = useState(cfg.design?.theme || {});
    const [slotValues, setSlotValues] = useState(() => {
        const out = {};
        (cfg.slots || []).forEach((s) => {
            out[s.id] = { label_en: s.label?.en || '', label_fr: s.label?.fr || '',
                          label_es: s.label?.es || '', bg: s.bg || '', fg: s.fg || '',
                          auto: s.auto || [],
                          visible: !s.hidden_by_owner };
        });
        return out;
    });
    const [screen, setScreen] = useState(startScreen || 'register');
    const [device, setDevice] = useState('phone');
    const [resetAsk, setResetAsk] = useState(false);
    const [box, width] = useOwnWidth();

    // Side by side ONLY when the editor itself is wide enough for both.
    const side = width >= 1180;
    const previewWidth = side ? Math.min(620, Math.round(width * 0.42)) : width;
    const controlWidth = side ? width - previewWidth - 24 : width;
    const cols = (wide, mid, narrow) => (controlWidth >= wide[0] ? wide[1]
        : controlWidth >= mid[0] ? mid[1] : narrow);
    const twoUp = cols([520, 'grid-cols-2'], [0, 'grid-cols-1'], 'grid-cols-1');
    const colourCols = cols([620, 'grid-cols-5'], [420, 'grid-cols-3'], 'grid-cols-2');

    const DEVICES = {
        phone: { w: 390, h: 844, max: 1 },
        tablet: { w: 1024, h: 768, max: 0.9 },
    };
    const frame = DEVICES[device];
    const room = Math.max(280, previewWidth) - 10;
    const frameScale = Math.max(0.3, Math.min(frame.max, room / frame.w));

    const screens = useMemo(
        () => [...new Set((cfg.slots || []).map((s) => s.screen))], [cfg.slots]);

    // The draft, in exactly the shape the portal reads.
    const draft = useMemo(() => ({
        ...cfg,
        design: { branding, theme },
        contact: { email: 'hello@example.com', phone: '+1 555 0100' },
        slots: (cfg.slots || []).map((s) => {
            const v = slotValues[s.id] || {};
            return { ...s,
                     label: { en: v.label_en || '', fr: v.label_fr || '', es: v.label_es || '' },
                     bg: v.bg || '', fg: v.fg || '',
                     visible: v.visible === false ? false : s.visible };
        }),
    }), [cfg, branding, theme, slotValues]);

    const setB = (key) => (value) => setBranding((b) => ({ ...b, [key]: value }));
    const setT = (key) => (value) => setTheme((d) => ({ ...d, [key]: value }));

    /** Back to the look the portal has when nothing is saved. Colours, logos
     *  and wording are NOT touched — only the text styles. */
    const resetTypography = () => setTheme((d) => ({ ...d, ...TYPOGRAPHY_DEFAULTS }));

    const save = () => onSave({ design: { branding, theme, slots: slotValues } });

    const image = (label, hint, kind, key, testid) => (
        <ImageField label={label} hint={hint} value={branding[key]} orgId={orgId}
                    apiPrefix="loyalty" endpoint="design/image" responseKey="url"
                    extraFields={{ kind }} testid={testid}
                    onChange={(url) => setB(key)(url || '')} />
    );

    return (
        <div className="space-y-5" data-testid="cc-loyalty-design" ref={box}>
            <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold">
                    {t('loyalty.design.title', 'Design the portal')}
                </h4>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <LookButton orgId={orgId} t={t} />
                    <Button size="sm" onClick={save} disabled={busy} data-testid="cc-loyalty-design-save">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        <span className="ml-2">{t('common.save', 'Save')}</span>
                    </Button>
                </div>
            </div>

            <div className={side ? 'grid gap-6' : 'flex flex-col gap-6'}
                 style={side ? { gridTemplateColumns: `minmax(0,1fr) ${previewWidth}px` } : undefined}
                 data-testid="cc-loyalty-design-layout" data-side={side ? '1' : '0'}>
                {/* ── the controls: always the full width they have ────── */}
                <div className="min-w-0 space-y-5" data-testid="cc-loyalty-design-controls">
                    <div className={`grid min-w-0 gap-3 ${twoUp}`}>
                        {image(t('loyalty.design.merchantLogo', 'Business logo'),
                               t('loyalty.design.merchantLogoHint', 'Falls back to your brand logo'),
                               'merchant_logo', 'merchant_logo_url', 'cc-loyalty-merchant-logo')}
                        {image(t('loyalty.design.programLogo', 'Program logo'), '',
                               'program_logo', 'program_logo_url', 'cc-loyalty-program-logo')}
                    </div>

                    <div className={`grid min-w-0 gap-3 ${twoUp}`}>
                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.merchantLogoSize', 'Business logo size')} — {branding.merchant_logo_px || 96}px
                            </span>
                            <input type="range" min={20} max={240} className="w-full"
                                   value={branding.merchant_logo_px || 96}
                                   data-testid="cc-loyalty-merchant-logo-size"
                                   onChange={(e) => setB('merchant_logo_px')(Number(e.target.value))} />
                        </label>
                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.programLogoSize', 'Program logo size')} — {branding.program_logo_px || 72}px
                            </span>
                            <input type="range" min={20} max={240} className="w-full"
                                   value={branding.program_logo_px || 72}
                                   data-testid="cc-loyalty-program-logo-size"
                                   onChange={(e) => setB('program_logo_px')(Number(e.target.value))} />
                        </label>
                    </div>

                    {image(t('loyalty.design.wallpaper', 'Wallpaper'), '',
                           'wallpaper', 'wallpaper_url', 'cc-loyalty-wallpaper')}

                    <div className={`grid min-w-0 gap-3 ${twoUp}`}>
                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.wallpaperFit', 'Wallpaper fit')}
                            </span>
                            <select className={field} value={branding.wallpaper_fit || 'fill'}
                                    data-testid="cc-loyalty-wallpaper-fit"
                                    onChange={(e) => setB('wallpaper_fit')(e.target.value)}>
                                <option value="fill">{t('loyalty.design.fitFill', 'Fill the screen')}</option>
                                <option value="whole">{t('loyalty.design.fitWhole', 'Whole picture')}</option>
                            </select>
                        </label>
                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.wallpaperZoom', 'Zoom')} — {branding.wallpaper_zoom || 100}%
                            </span>
                            <input type="range" min={100} max={200} className="w-full"
                                   value={branding.wallpaper_zoom || 100}
                                   data-testid="cc-loyalty-wallpaper-zoom"
                                   onChange={(e) => setB('wallpaper_zoom')(Number(e.target.value))} />
                        </label>
                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.wallpaperDark', 'Darkness')} — {branding.wallpaper_dark ?? 55}%
                            </span>
                            <input type="range" min={0} max={90} className="w-full"
                                   value={branding.wallpaper_dark ?? 55}
                                   data-testid="cc-loyalty-wallpaper-dark"
                                   onChange={(e) => setB('wallpaper_dark')(Number(e.target.value))} />
                        </label>
                        <div>
                            <span className="mb-1 block text-[11px] text-slate-500">
                                {t('loyalty.design.wallpaperPosition', 'Position')}
                            </span>
                            <div className="grid w-24 grid-cols-3 gap-1" data-testid="cc-loyalty-wallpaper-position">
                                {[0, 50, 100].map((y) => [0, 50, 100].map((x) => {
                                    const on = (branding.wallpaper_x ?? 50) === x
                                        && (branding.wallpaper_y ?? 50) === y;
                                    return (
                                        <button key={`${x}-${y}`} type="button"
                                                data-testid={`cc-loyalty-wallpaper-pos-${x}-${y}`}
                                                onClick={() => setBranding((b) => ({ ...b, wallpaper_x: x, wallpaper_y: y }))}
                                                className={`h-6 rounded border ${on ? 'bg-fuchsia-500 border-fuchsia-500' : 'border-slate-300 dark:border-slate-600'}`} />
                                    );
                                }))}
                            </div>
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-wrap items-center gap-4"
                         data-testid="cc-loyalty-looks">
                        <label className="flex items-center gap-2 text-[11px]">
                            <input type="checkbox" checked={!!branding.show_icons}
                                   data-testid="cc-loyalty-show-icons"
                                   onChange={(e) => setB('show_icons')(e.target.checked)} />
                            {t('loyalty.design.showIcons', 'Show icons on buttons')}
                        </label>
                        <label className="flex items-center gap-2 text-[11px]">
                            <input type="checkbox" checked={!!branding.lux_buttons}
                                   data-testid="cc-loyalty-lux-buttons"
                                   onChange={(e) => setB('lux_buttons')(e.target.checked)} />
                            {t('loyalty.design.luxButtons', 'Metallic and glass buttons')}
                        </label>
                        <label className="flex items-center gap-2 text-[11px]">
                            <input type="checkbox" checked={branding.desk_lights !== false}
                                   data-testid="cc-loyalty-desk-lights"
                                   onChange={(e) => setB('desk_lights')(e.target.checked)} />
                            {t('loyalty.design.deskLights', 'Show lights')}
                        </label>
                    </div>

                    <div className={`grid min-w-0 gap-3 ${colourCols}`}>
                        <Colour label={t('loyalty.design.bg', 'Background')} value={theme.bg}
                                onChange={setT('bg')} testid="cc-loyalty-colour-bg" />
                        <Colour label={t('loyalty.design.text', 'Text')} value={theme.text}
                                onChange={setT('text')} testid="cc-loyalty-colour-text" />
                        <Colour label={t('loyalty.design.accent', 'Accent')} value={theme.accent}
                                onChange={setT('accent')} testid="cc-loyalty-colour-accent" />
                        <Colour label={t('loyalty.design.button', 'Buttons')} value={theme.button}
                                onChange={setT('button')} testid="cc-loyalty-colour-button" />
                        <Colour label={t('loyalty.design.buttonText', 'Button text')} value={theme.button_text}
                                onChange={setT('button_text')} testid="cc-loyalty-colour-button-text" />
                    </div>

                    <div className="min-w-0 space-y-3 rounded-xl border border-slate-200 p-3
                                    dark:border-slate-700" data-testid="cc-loyalty-typography">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold">
                                {t('loyalty.design.type.title', 'Typography')}
                            </p>
                            {resetAsk ? (
                                <span className="flex items-center gap-2 text-[11px]">
                                    {t('loyalty.design.type.resetAsk', 'Put every text style back to the defaults?')}
                                    <Button size="sm" variant="destructive" data-testid="cc-loyalty-type-reset-go"
                                            onClick={() => { resetTypography(); setResetAsk(false); }}>
                                        {t('common.confirm', 'Confirm')}
                                    </Button>
                                    <Button size="sm" variant="ghost" data-testid="cc-loyalty-type-reset-cancel"
                                            onClick={() => setResetAsk(false)}>
                                        {t('common.cancel', 'Cancel')}
                                    </Button>
                                </span>
                            ) : (
                                <Button size="sm" variant="outline" data-testid="cc-loyalty-type-reset"
                                        onClick={() => setResetAsk(true)}>
                                    {t('loyalty.design.type.reset', 'Reset typography')}
                                </Button>
                            )}
                        </div>

                        <label className="block min-w-0">
                            <span className="mb-1 block break-words text-[11px] text-slate-500">
                                {t('loyalty.design.type.scale', 'Text size')} — {theme.text_scale ?? 100}%
                            </span>
                            <input type="range" min={80} max={160} step={5} className="w-full"
                                   value={theme.text_scale ?? 100}
                                   data-testid="cc-loyalty-text-scale"
                                   onChange={(e) => setT('text_scale')(Number(e.target.value))} />
                        </label>

                        <StyleRow id="headline" label={t('loyalty.design.type.headline', 'Headline')}
                                  withAlign theme={theme} setT={setT} t={t} />
                        <StyleRow id="body" label={t('loyalty.design.type.body', 'Body')} withAlign
                                  theme={theme} setT={setT} t={t} />
                        <StyleRow id="button" label={t('loyalty.design.type.buttons', 'Buttons')}
                                  colourKey="button_text" theme={theme} setT={setT} t={t} />
                        <StyleRow id="small" label={t('loyalty.design.type.small', 'Small print')}
                                  theme={theme} setT={setT} t={t} />
                    </div>

                    <div>
                        <Label className="mb-2 block text-xs">
                            {t('loyalty.design.wording', 'Buttons and wording on this screen')}
                        </Label>
                        <div className="space-y-2">
                            {(cfg.slots || []).filter((s) => s.screen === screen).map((s) => (
                                <SlotEditor key={s.id} slot={s} theme={theme}
                                            narrow={controlWidth < 560}
                                            value={slotValues[s.id] || {}}
                                            onChange={(v) => setSlotValues((all) => ({ ...all, [s.id]: v }))} />
                            ))}
                        </div>
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('loyalty.design.giftLook', 'Gift cards keep the look you saved for your gift cards.')}{' '}
                        <a href="/voucher-settings" className="underline" data-testid="cc-loyalty-gift-design-link">
                            {t('loyalty.design.giftLookLink', 'Open the gift card design')}
                        </a>
                    </p>
                </div>

                {/* ── the live preview: the REAL portal, on the draft ───── */}
                <div className="min-w-0 space-y-3" data-testid="cc-loyalty-design-preview">
                    <div className="flex flex-wrap gap-1" data-testid="cc-loyalty-preview-screens">
                        {screens.map((s) => (
                            <button key={s} type="button" onClick={() => setScreen(s)}
                                    data-testid={`cc-loyalty-preview-${s}`}
                                    className={`rounded-full px-3 py-1 text-[11px] ${screen === s
                                        ? 'bg-fuchsia-500 text-white'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                                {t(`loyalty.screen.${s}`, s)}
                            </button>
                        ))}
                    </div>

                    {/* ONE preview at a time, at a size that can be read. */}
                    <div className="flex flex-wrap items-center gap-2"
                         data-testid="cc-loyalty-preview-devices">
                        <button type="button" onClick={() => setDevice('phone')}
                                data-testid="cc-loyalty-preview-device-phone"
                                className={`flex items-center gap-1 rounded-full px-3 py-1 text-[11px] ${
                                    device === 'phone' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                            <Smartphone className="h-3 w-3" /> {t('loyalty.design.phone', 'Phone')}
                        </button>
                        <button type="button" onClick={() => setDevice('tablet')}
                                data-testid="cc-loyalty-preview-device-tablet"
                                className={`flex items-center gap-1 rounded-full px-3 py-1 text-[11px] ${
                                    device === 'tablet' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                            <Tablet className="h-3 w-3" /> {t('loyalty.design.tablet', 'iPad')}
                        </button>
                    </div>

                    <Frame width={frame.w} height={frame.h} scale={frameScale}
                           testid={`cc-loyalty-preview-${device}`}>
                        <PortalView screen={screen} cfg={draft} preview framed
                                    me={SAMPLE.me} saved={SAMPLE.saved} form={SAMPLE.form} />
                    </Frame>
                </div>
            </div>
        </div>
    );
};
